import { useState, type FormEvent } from 'react';
import { recordsApi, type AutomaticTranscriptsView, type CdrSender, type TranscriptionNightView, type VoicemailCheck } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice, SelectField } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { allZones } from '../../lib/time';
import { automaticLines, nightLabel } from '../../lib/transcription';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System: the call records service (its own container), which takes the call
 * records the phone system pushes and sends voicemail emails. Running or not, the last record,
 * today's counts, and what it has refused (a wrong password in the CDR URL shows here first).
 * Transcription: the main line (asked-for recordings, voicemail), and automatic recordings (what's
 * waiting, being written out, finished, failed, the night's catch-up), with the night window.
 */
export function RecordsServiceCard() {
  const status = useData(() => recordsApi.status(), []);
  const s = status.data;
  if (status.error) {
    return (
      <Card title="Call records">
        <LoadError error={status.error} retry={status.reload} />
      </Card>
    );
  }
  if (!s) {
    return (
      <Card title="Call records">
        <Loading />
      </Card>
    );
  }
  const running = s.service.running;
  const c = s.service.counts;
  const queue = s.transcription;
  const waiting = (q: Record<string, number> | undefined) => (q ? (q.waiting ?? 0) + (q.prioritized ?? 0) + (q.delayed ?? 0) : 0);
  return (
    <Card
      title="Call records"
      foot={
        <span className="muted" style={{ fontSize: 13 }}>
          Its own container (apps/records). The phone system’s CDR URL gets webcdrs://records.&lt;domain&gt;/cdr next to Analytics, separated by a space; CDR_ALLOW_IPS
          limits it to the phone system’s address (or use a password: user:password@ or /cdr/&lt;password&gt;). See docs/call-records.md.
        </span>
      }
    >
      <div className="card-body">
        <dl className="dl">
          <dt>Service</dt>
          <dd>
            {running.length ? (
              <Chip tone="ok">Running{running.length > 1 ? ` (${running.length})` : ''}</Chip>
            ) : s.service.lastSeenAt ? (
              <Chip tone="crit">Not running</Chip>
            ) : (
              <Chip>Not set up</Chip>
            )}
            {running[0] && <small className="muted"> {running[0].version} · since {shortWhen(running[0].startedAt)}</small>}
            {!running.length && s.service.lastSeenAt && <small className="muted"> last seen {shortWhen(s.service.lastSeenAt)}</small>}
          </dd>
          {running.length > 0 && (c.openToAnyone ?? 0) > 0 && (
            <>
              <dt>Who can send records</dt>
              <dd>
                <Chip tone="warn" small>
                  Anyone
                </Chip>{' '}
                <small className="muted">No senders below, no CDR_ALLOW_IPS and no CDR_PASSWORD: add the phone system’s address below.</small>
              </dd>
            </>
          )}
          <dt>Last record received</dt>
          <dd>{s.lastRecordAt ? shortWhen(s.lastRecordAt) : <span className="muted">None yet</span>}</dd>
          <dt>Records today</dt>
          <dd>{s.recordsToday.toLocaleString()}</dd>
          <dt>Voicemail emails today</dt>
          <dd>
            {s.voicemailEmailsToday.sent.toLocaleString()} sent
            {s.voicemailEmailsToday.failed > 0 && (
              <>
                {' '}
                <Chip tone="warn" small>
                  {s.voicemailEmailsToday.failed} didn’t go out
                </Chip>
              </>
            )}
            {(s.voicemailEmailsToday.withoutAudio ?? 0) > 0 && (
              <>
                {' '}
                <Chip tone="warn" small>
                  {s.voicemailEmailsToday.withoutAudio} without the audio
                </Chip>
                {s.voicemailEmailsToday.withoutAudioWhy && <div className="muted small">Latest: {s.voicemailEmailsToday.withoutAudioWhy.replace(/^Sent without the audio: /, '')}</div>}
              </>
            )}
          </dd>
          {running.length > 0 && (c.refusedAuth > 0 || c.refusedAddress > 0 || c.invalid > 0) && (
            <>
              <dt>Refused since it started</dt>
              <dd>
                {c.refusedAuth > 0 && <div>{c.refusedAuth} with a wrong or missing user name and password</div>}
                {c.refusedAddress > 0 && <div>{c.refusedAddress} from an address not on the list (the records log shows which)</div>}
                {c.invalid > 0 && <div>{c.invalid} that weren’t call records</div>}
              </dd>
            </>
          )}
          {running.length > 0 && c.uploads > 0 && (
            <>
              <dt>Recording files ignored</dt>
              <dd>{c.uploads.toLocaleString()}</dd>
            </>
          )}
          {queue && (
            <>
              <dt>Transcription</dt>
              <dd>
                {waiting(queue.main)} waiting, {queue.main.active ?? 0} being written out
              </dd>
            </>
          )}
          {s.automatic && <Automatic a={s.automatic} reload={status.reload} />}
        </dl>
      </div>
      <Senders />
      <CheckMailbox />
    </Card>
  );
}

const EMAIL_STATUS: Record<string, string> = { sent: 'Sent', failed: 'Didn’t go out', skipped: 'Not sent', sending: 'Being sent' };

/**
 * Check a mailbox (1.12.26): why its voicemail emails go with or without the audio. A person's
 * extension or a shared box, by its number.
 */
function CheckMailbox() {
  const [tenant, setTenant] = useState('');
  const [mailbox, setMailbox] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [r, setR] = useState<VoicemailCheck | null>(null);
  async function check(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    setR(null);
    try {
      setR(await recordsApi.checkVoicemail(tenant.trim(), mailbox.trim()));
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  return (
    <div className="card-body records-senders">
      <h3 className="records-senders-title">Check a mailbox’s voicemail emails</h3>
      <p className="muted" style={{ margin: 0 }}>
        Why its emails go with or without the audio: a person’s extension or a shared box, by its number. It looks at the latest message (read only; in the company’s Activity).
      </p>
      <form onSubmit={(e) => void check(e)} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Company" placeholder="acme.stitchuc.com" value={tenant} onChange={(e) => setTenant(e.target.value)} />
        <Field label="Mailbox" placeholder="290" value={mailbox} maxLength={32} onChange={(e) => setMailbox(e.target.value)} />
        <Button type="submit" disabled={busy || !tenant.trim() || !mailbox.trim()}>
          {busy ? 'Checking…' : 'Check'}
        </Button>
      </form>
      {problem && <Notice tone="crit">{problem}</Notice>}
      {r && (
        <>
          <Notice tone={r.verdict.ok ? 'ok' : 'warn'}>{r.verdict.text}</Notice>
          <dl className="dl">
            <dt>Mailbox</dt>
            <dd>
              {r.mailbox} at {r.tenant} {r.shared ? <Chip small>Shared box</Chip> : null}
            </dd>
            <dt>Stitch’s emails</dt>
            <dd>{!r.emails.on ? 'Off at this company' : !r.emails.send ? 'Off for this mailbox' : r.emails.attach ? 'On, with the audio' : 'On, without the audio'}</dd>
            <dt>Attach the audio</dt>
            <dd>
              {r.emails.attach ? 'Yes' : 'No'}{' '}
              <small className="muted">
                {r.emails.attachFrom === 'mailbox' ? '(chosen for this mailbox)' : `(the company’s default, ${r.emails.companyAttach ? 'on' : 'off'})`}
              </small>
            </dd>
            <dt>Email addresses on it</dt>
            <dd>{r.addresses === null ? 'Couldn’t be read' : r.addresses}</dd>
            <dt>Latest message</dt>
            <dd>
              {r.listProblem
                ? `Couldn’t be read: ${r.listProblem}`
                : r.latest
                  ? `${shortWhen(r.latest.at)} · ${r.latest.seconds} s · ${r.latest.listedWithAudio ? 'listed with audio' : 'listed without audio'}`
                  : 'None'}
            </dd>
            {r.audio && (
              <>
                <dt>Its audio</dt>
                <dd>
                  {r.audio.ok ? (
                    <Chip tone="ok" small>
                      {r.audio.tooLarge ? 'Too large to attach' : 'Can be attached'}
                    </Chip>
                  ) : (
                    <Chip tone="crit" small>
                      Can’t be attached
                    </Chip>
                  )}{' '}
                  <small className="muted">
                    {[r.audio.file, r.audio.type, r.audio.bytes !== undefined ? kb(r.audio.bytes) : null, r.audio.ok ? null : r.audio.why].filter(Boolean).join(' · ')}
                  </small>
                </dd>
              </>
            )}
            {r.latest && (
              <>
                <dt>Its email</dt>
                <dd>
                  {r.emailed ? (
                    <>
                      {EMAIL_STATUS[r.emailed.status] ?? r.emailed.status}
                      {r.emailed.status === 'sent' ? ` to ${r.emailed.recipients}` : ''}
                      {r.emailed.note && <small className="muted"> · {r.emailed.note}</small>}
                    </>
                  ) : (
                    <span className="muted">None in the log</span>
                  )}
                </dd>
              </>
            )}
          </dl>
        </>
      )}
    </div>
  );
}

/** Automatic recordings: the numbers, and the night window (Stitch staff change it here). */
function Automatic({ a, reload }: { a: AutomaticTranscriptsView; reload: () => void }) {
  const lines = automaticLines(a);
  return (
    <>
      <dt>Automatic recordings</dt>
      <dd>
        {lines.main}
        {lines.chips.map((c) => (
          <span key={c.text}>
            {' '}
            <Chip tone={c.tone} small>
              {c.text}
            </Chip>
          </span>
        ))}
        {lines.notes.map((t) => (
          <small key={t} className="muted" style={{ display: 'block' }}>
            {t}
          </small>
        ))}
      </dd>
      <dt>Night catch-up</dt>
      <dd>
        <NightWindow night={a.night} saved={reload} />
      </dd>
    </>
  );
}

/**
 * When automatic recordings catch up: more at once, failed ones tried again, and recorded calls
 * with no transcript found again. Read by the call records service within seconds.
 */
function NightWindow({ night, saved }: { night: TranscriptionNightView; saved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState(night.nightStart);
  const [end, setEnd] = useState(night.nightEnd);
  const [zone, setZone] = useState(night.zone);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblem('');
    try {
      await recordsApi.saveNight({ nightStart: start, nightEnd: end, zone });
      setEditing(false);
      saved();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <>
        {nightLabel(night)}{' '}
        <Button
          size="small"
          variant="ghost"
          icon="edit"
          onClick={() => {
            setStart(night.nightStart);
            setEnd(night.nightEnd);
            setZone(night.zone);
            setEditing(true);
          }}
        >
          Change
        </Button>
        <small className="muted" style={{ display: 'block' }}>
          Recordings are written out more at a time then, and ones that failed are tried again.{night.saved ? ` Saved ${shortWhen(night.saved.at)}${night.saved.byName ? ` by ${night.saved.byName}` : ''}.` : ''}
        </small>
      </>
    );
  }
  const zones = allZones();
  return (
    <form className="records-night" onSubmit={(e) => void save(e)}>
      <Field label="Night starts" type="time" value={start} onChange={(e) => setStart(e.target.value)} required />
      <Field label="Night ends" type="time" value={end} onChange={(e) => setEnd(e.target.value)} required />
      <SelectField
        label="Time zone"
        value={zone}
        onChange={(e) => setZone(e.target.value)}
        options={zones.some((z) => z.value === zone) ? zones : [{ value: zone, label: zone }, ...zones]}
      />
      <div className="records-night-actions">
        <Button type="submit" size="small" variant="primary" disabled={busy || !start || !end}>
          Save
        </Button>
        <Button size="small" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
      {problem && <Notice tone="crit">{problem}</Notice>}
    </form>
  );
}

/**
 * Who may send call records: the phone system's addresses (IPs or ranges). The records service
 * reads this list every minute, beside CDR_ALLOW_IPS (which still counts). Each change is in the
 * staff log.
 */
function Senders() {
  const list = useData(() => recordsApi.senders(), []);
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [senders, setSenders] = useState<CdrSender[] | null>(null);
  const shown = senders ?? list.data?.senders ?? null;
  const run = async (fn: () => Promise<{ senders: CdrSender[] }>) => {
    setBusy(true);
    setProblem('');
    try {
      setSenders((await fn()).senders);
      return true;
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    if (await run(() => recordsApi.addSender(address.trim(), note.trim()))) {
      setAddress('');
      setNote('');
    }
  };
  return (
    <div className="card-body records-senders">
      <h3 className="records-senders-title">Who may send call records</h3>
      <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
        The phone system’s addresses. Records from anywhere else are refused (unless the CDR URL’s password is the check). Read by the records service every minute.
      </p>
      {list.error && !shown ? (
        <LoadError error={list.error} retry={list.reload} />
      ) : !shown ? (
        <Loading />
      ) : shown.length ? (
        <ul className="records-sender-list" aria-label="Addresses that may send call records">
          {shown.map((s) => (
            <li key={s.address}>
              <span className="mono">{s.address}</span>
              {s.note && <span className="muted">{s.note}</span>}
              <Button size="small" variant="ghost" icon="trash" disabled={busy} onClick={() => void run(() => recordsApi.removeSender(s.address))} aria-label={`Remove ${s.address}`}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted" style={{ margin: 0 }}>None added here (CDR_ALLOW_IPS still counts).</p>
      )}
      <form className="records-sender-add" onSubmit={(e) => void add(e)}>
        <Field label="Address or range" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="203.0.113.10 or 198.51.100.0/24" maxLength={64} />
        <Field label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Phone system, us-east" maxLength={200} />
        <Button type="submit" size="small" disabled={busy || !address.trim()}>
          Add
        </Button>
      </form>
      {problem && <Notice tone="crit">{problem}</Notice>}
    </div>
  );
}
